import type {
  AtualizarCondicaoPagamentoDto, CondicaoPagamentoResponse, CriarCondicaoPagamentoDto, GerarParcelasDto, GerarParcelasResponse,
  ListarCondicoesPagamentoQuery, RegenerarCondicaoDto,
} from '@triade/contracts';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ApiError, requisitar } from '@/lib/api';
import { useSessao } from '@/lib/session';
import { useConsulta, useMutacao } from './base';

export function useCondicoesPagamento(q: Partial<ListarCondicoesPagamentoQuery> = {}) {
  return useConsulta<CondicaoPagamentoResponse[]>(['condicoes-pagamento'], '/condicoes-pagamento', { query: q });
}

/** Uma condicao com o detalhe (grade `parcelas`, `prazoMedio`, `emUso`). GET /condicoes-pagamento/:id: `tabela.ver`. */
export function useCondicaoPagamento(id: string | undefined) {
  return useConsulta<CondicaoPagamentoResponse>(['condicoes-pagamento', id], `/condicoes-pagamento/${id}`, { ativo: !!id });
}

/**
 * Previa da grade pela API (POST /condicoes-pagamento/gerar, nao grava). So roda com `params` validos (null = desligado).
 * A tela nunca monta a grade: mostra o que vier daqui, incluindo `prazoMedio`.
 */
export function useGerarParcelas(params: GerarParcelasDto | null) {
  const { empresaId } = useSessao();
  return useQuery<GerarParcelasResponse, ApiError>({
    queryKey: ['condicoes', 'gerar', params],
    queryFn: () => requisitar<GerarParcelasResponse>('/condicoes-pagamento/gerar', { method: 'POST', body: params }),
    enabled: params !== null && empresaId !== null,
    placeholderData: keepPreviousData,
  });
}

export function useCriarCondicaoPagamento() {
  return useMutacao<CondicaoPagamentoResponse, CriarCondicaoPagamentoDto>({
    metodo: 'POST', caminho: () => '/condicoes-pagamento', corpo: (v) => v, invalidar: [['condicoes-pagamento']],
  });
}

/** Nome e situacao apenas: parcelas e tipo mudam por Regenerar. */
export function useAtualizarCondicaoPagamento() {
  return useMutacao<CondicaoPagamentoResponse, { id: string } & AtualizarCondicaoPagamentoDto>({
    metodo: 'PATCH',
    caminho: (v) => `/condicoes-pagamento/${v.id}`,
    corpo: ({ id: _id, ...resto }) => resto,
    invalidar: [['condicoes-pagamento']],
  });
}

/**
 * Regenerar (POST /condicoes-pagamento/:id/regenerar): condicao sem uso troca no lugar; usada cria nova versao
 * (o `id` da resposta pode ser novo). `tabela.editar`.
 */
export function useRegenerarCondicao() {
  return useMutacao<CondicaoPagamentoResponse, { id: string } & RegenerarCondicaoDto>({
    metodo: 'POST',
    caminho: (v) => `/condicoes-pagamento/${v.id}/regenerar`,
    corpo: ({ id: _id, ...resto }) => resto,
    invalidar: [['condicoes-pagamento'], ['condicoes']],
  });
}
